import { faker } from '@faker-js/faker';
import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { generateProfile, generateCourse } from '@/test/data-generators';
import { db } from '@/test/mocks/db';
import {
  MOCK_APEX_VARIANT_ID,
  MOCK_APEX_VARIANT_ID2,
  MOCK_CURRENT_BATCH,
  networkDelay,
  requireAuth,
} from '@/test/mocks/utils';

import { ProfilesResponse } from '../types';

// Mock state to track selected profile and variant across API calls
export const mockProfileState = {
  selectedProfileId: null as string | null,
  selectedVariantId: null as string | null,

  // Helper methods for managing state
  setSelectedProfile: (profileId: string) => {
    mockProfileState.selectedProfileId = profileId;
  },

  clearSelectedProfile: () => {
    mockProfileState.selectedProfileId = null;
  },

  getSelectedProfile: () => mockProfileState.selectedProfileId,

  setSelectedVariant: (variantId: string) => {
    mockProfileState.selectedVariantId = variantId;
  },

  clearSelectedVariant: () => {
    mockProfileState.selectedVariantId = null;
  },

  getSelectedVariant: () => mockProfileState.selectedVariantId,
};

export const getProfiles = (): Promise<ProfilesResponse.Root> => {
  return axios.get(`/user/v1/profiles`);
};

// Banner-showcase payment metadata: positive offerExpiresInSeconds → active
// offer (purple countdown); negative → expired offer (red "trial has ended").
// Spread and override is_payment_due: false to hide the banner.
const paymentMetadata = (
  offerExpiresInSeconds: number
): ProfilesResponse.EnrolledCourseMetadata => ({
  grouped_schedule_status: 'CREATED',
  is_payment_due: true,
  bhanzu_payment_url: 'https://payments.dev.bhanzu.com/pay/apex',
  currency: 'INR',
  amount_payable: { value: '2499' },
  amount_total: { value: '2999' },
  offer: {
    value: '500',
    expires_at: Math.floor(Date.now() / 1000) + offerExpiresInSeconds,
  },
});

export const getProfilesMockHandler = http.get(
  `${env.API_URL}/user/v1/profiles`,
  async ({ cookies }): Promise<HttpResponse<ProfilesResponse.Root>> => {
    await networkDelay();
    const { error } = requireAuth(cookies);
    if (error) {
      return HttpResponse.json(
        {
          message: error,
          consent_granted: false,
          logged_in_user: '',
          primary_profile: '',
          profiles: [],
        },
        { status: 401 }
      );
    }

    // Get all profiles from the database
    const profiles = db.profile.getAll();
    const users = db.user.getAll();

    // Find the first user (in real app, this would be based on auth token)
    const [currentUser] = users;
    const userProfiles = profiles.filter(
      profile => profile.base_lead_id === currentUser?.base_lead_id
    );

    if (userProfiles.length === 0 && currentUser) {
      const onboardingProfileId = faker.string.uuid();
      const normalProfileId1 = faker.string.uuid();
      const normalProfileId2 = faker.string.uuid();
      const parentProfileId = faker.string.uuid();
      const grade = faker.helpers.arrayElement(['2', '3', '4', '5', '6']);

      // Onboarding-eligible courses: sd_onboarding=true, status=active, NO batch_id
      const course1 = generateCourse({
        status: 'active',
        course_name: 'Math Wizard',
        category: 'live',
        sd_onboarding: true,
        batch_id: '',
        variant_id: faker.string.uuid(),
        profile_id: onboardingProfileId,
      });

      const course2 = generateCourse({
        status: 'active',
        course_name: 'English',
        category: 'live',
        sd_onboarding: true,
        batch_id: '',
        variant_id: faker.string.uuid(),
        profile_id: onboardingProfileId,
      });

      // Non-onboarding courses (batch already assigned)
      const mathWizardVariantId = faker.string.uuid();
      const course3 = generateCourse({
        status: 'active',
        course_name: faker.helpers.arrayElement([
          'Grade 5 Academic Maths',
          'School Curriculum',
          'Mental Arithmetic',
        ]),
        category: 'live',
        sd_onboarding: false,
        batch_id: MOCK_CURRENT_BATCH.id,
        variant_id: mathWizardVariantId,
        profile_id: normalProfileId1,
        is_trial: false, // Explicit so it isn't randomized by the generator
        // To test payment scenarios with this course:
        // 1. Set for_scheduled_payments: true
        // 2. Add metadata with is_payment_due: true
        for_scheduled_payments: false,
      });

      const course4 = generateCourse({
        status: 'active',
        course_name: faker.helpers.arrayElement([
          'Advanced Problem Solving',
          'Vedic Maths',
          'Competitive Maths',
        ]),
        category: 'live',
        sd_onboarding: false,
        batch_id: MOCK_CURRENT_BATCH.id,
        variant_id: faker.string.uuid(),
        profile_id: normalProfileId2,
        is_trial: true,
        for_scheduled_payments: true,
        // SHOWCASE: expired-offer payment banner (red, "trial has ended").
        // Switch to the Esther Wilderman profile to see it.
        metadata: paymentMetadata(-60 * 60),
      });

      // Apex Academy course - customize is_trial, for_scheduled_payments, metadata for testing
      const apexCourse = generateCourse({
        status: 'active',
        course_name: 'Apex Academy Grade7 - Trial',
        product_name: 'Apex Academy Test',
        category: 'live',
        sd_onboarding: false,
        batch_id: MOCK_CURRENT_BATCH.id,
        variant_id: MOCK_APEX_VARIANT_ID,
        profile_id: normalProfileId1,
        is_trial: true, // Change this to test trial scenarios
        for_scheduled_payments: true, // Change this to test payment scenarios
        // Payment settled on this course; Taylor's SELECTED apex course below
        // carries the showcase banner instead.
        metadata: { ...paymentMetadata(24 * 60 * 60), is_payment_due: false },
      });
      const apexCourse2 = generateCourse({
        status: 'active',
        course_name: 'Apex Academy',
        product_name: 'Apex Academy',
        category: 'live',
        sd_onboarding: false,
        batch_id: MOCK_CURRENT_BATCH.id,
        variant_id: MOCK_APEX_VARIANT_ID2,
        profile_id: normalProfileId1,
        is_trial: true, // Change this to test trial scenarios
        for_scheduled_payments: true, // Change this to test payment scenarios
        // SHOWCASE: active-offer payment banner (purple, live countdown).
        // Taylor has this course selected, so the dashboard shows the offer
        // state. Expires in 2d 3h 45m so every countdown chip has a value.
        metadata: paymentMetadata(((2 * 24 + 3) * 60 + 45) * 60),
        // 0 credits so the "trial completed" card shows for this selected trial
        // course (gated on trial + no payment due + no upcoming sessions + 0 credits).
        current_credit: {
          bought_credit: 0,
          bhanzu_money_credit: 0,
          free_credit: 0,
        },
      });

      const generatedCourse1 = db.course.create(course1);
      const generatedCourse2 = db.course.create(course2);
      const generatedCourse3 = db.course.create(course3);
      const generatedCourse4 = db.course.create(course4);
      const generatedApexCourse = db.course.create(apexCourse);
      const generatedApexCourse2 = db.course.create(apexCourse2);

      // Profile 1: Onboarding candidate (triggers onboarding flow)
      const studentProfileData1 = generateProfile({
        id: onboardingProfileId,
        name: 'Esther Abbott',
        email: currentUser.email,
        role: 'STUDENT',
        base_lead_id: currentUser.base_lead_id,
        practice_id: currentUser.practice_id,
        doubtgpt_enabled: true,
        onboarding_candidate: true,
        country: faker.helpers.arrayElement(['IN', 'US', 'GB', 'SG']),
        grade,
        absolute_grade: grade,
        first_registered: [],
      });

      const studentProfile1 = db.profile.create({
        ...studentProfileData1,
        enrolled_courses: [generatedCourse1, generatedCourse2],
        selected_variant_id: generatedCourse1.variant_id,
      } as Parameters<typeof db.profile.create>[0]);

      // Profile 2: Taylor - Normal student with multiple courses including Apex
      const studentProfileData2 = generateProfile({
        id: normalProfileId1,
        name: 'Taylor Bergstrom',
        email: currentUser.email,
        role: 'STUDENT',
        base_lead_id: currentUser.base_lead_id,
        practice_id: currentUser.practice_id,
        doubtgpt_enabled: true,
        onboarding_candidate: false,
        first_registered: [],
      });

      // Taylor always has multiple courses including Apex for testing scenarios
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const studentProfile2 = db.profile.create({
        ...studentProfileData2,
        enrolled_courses: [
          generatedCourse3,
          generatedApexCourse,
          generatedApexCourse2,
        ],
        selected_variant_id: generatedApexCourse2.variant_id,
      } as any);

      // Profile 3: Esther Wilderman - Another normal student
      const studentProfileData3 = generateProfile({
        id: normalProfileId2,
        name: 'Esther Wilderman',
        email: currentUser.email,
        role: 'STUDENT',
        base_lead_id: currentUser.base_lead_id,
        practice_id: currentUser.practice_id,
        doubtgpt_enabled: true,
        onboarding_candidate: false,
        first_registered: [],
      });

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const studentProfile3 = db.profile.create({
        ...studentProfileData3,
        enrolled_courses: [generatedCourse4],
        selected_variant_id: generatedCourse4.variant_id,
      } as any);

      // Profile 4: Parent
      const parentProfileData = generateProfile({
        id: parentProfileId,
        email: currentUser.email,
        role: 'PARENT',
        base_lead_id: currentUser.base_lead_id,
        practice_id: currentUser.practice_id,
        grade: undefined,
        doubtgpt_enabled: false,
        onboarding_candidate: false,
        first_registered: [],
      });

      const parentProfilePayload: Parameters<typeof db.profile.create>[0] = {
        ...parentProfileData,
        first_registered: [],
        enrolled_courses: [],
      };

      const parentProfile = db.profile.create(parentProfilePayload);

      userProfiles.push(
        studentProfile1,
        studentProfile2,
        studentProfile3,
        parentProfile
      );
    }

    // Transform profiles to match the expected format
    const transformedProfiles: ProfilesResponse.Profile[] = userProfiles.map(
      profile => ({
        ...profile,
        role: profile.role as 'PARENT' | 'STUDENT',
        // Keep enrolled courses as-is (metadata is already set in the mock).
        // The db models widen literal unions (e.g. grouped_schedule_status)
        // to plain strings, so narrow back to the API type here.
        enrolled_courses:
          profile.enrolled_courses as unknown as ProfilesResponse.EnrolledCourse[],
      })
    );

    const defaultStudentProfile = transformedProfiles.find(
      p => p.role === 'STUDENT' && !p.onboarding_candidate
    );

    return HttpResponse.json({
      status: 'Success',
      message: 'Profiles retrieved successfully',
      profiles: transformedProfiles,
      logged_in_user: currentUser?.id || '',
      primary_profile: transformedProfiles[0]?.id || '',
      selected_profile:
        mockProfileState.selectedProfileId ??
        defaultStudentProfile?.id ??
        transformedProfiles[0]?.id ??
        '',
      selected_variant_id:
        mockProfileState.selectedVariantId ??
        defaultStudentProfile?.selected_variant_id ??
        transformedProfiles[0]?.selected_variant_id ??
        '',
      consent_granted: true,
    });
  }
);
